/**
 * Мінімальна обгортка над IndexedDB.
 *
 * Чому не localStorage: тут зберігаються не лише налаштування, а й записи
 * власної вимови (Blob) та історія повторень — це десятки тисяч записів.
 * Чому не бібліотека: потрібні чотири операції, і залежність того не варта.
 */

const DB_NAME = 'polski'
const DB_VERSION = 1

export const STORE_KV = 'kv'
export const STORE_CARDS = 'cards'
export const STORE_LOG = 'log'
export const STORE_AUDIO = 'recordings'

let dbPromise: Promise<IDBDatabase> | null = null

function open(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE_KV)) db.createObjectStore(STORE_KV)
      if (!db.objectStoreNames.contains(STORE_CARDS)) db.createObjectStore(STORE_CARDS, { keyPath: 'id' })
      if (!db.objectStoreNames.contains(STORE_LOG)) {
        db.createObjectStore(STORE_LOG, { keyPath: 'at' })
      }
      if (!db.objectStoreNames.contains(STORE_AUDIO)) db.createObjectStore(STORE_AUDIO)
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
  return dbPromise
}

function tx<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(store, mode)
        const req = fn(t.objectStore(store))
        req.onsuccess = () => resolve(req.result)
        req.onerror = () => reject(req.error)
      }),
  )
}

export const kv = {
  get: <T>(key: string) => tx<T>(STORE_KV, 'readonly', (s) => s.get(key) as IDBRequest<T>),
  set: (key: string, value: unknown) =>
    tx(STORE_KV, 'readwrite', (s) => s.put(value, key) as IDBRequest<IDBValidKey>),
  del: (key: string) => tx(STORE_KV, 'readwrite', (s) => s.delete(key) as IDBRequest<undefined>),
}

export const cards = {
  all: <T>() => tx<T[]>(STORE_CARDS, 'readonly', (s) => s.getAll() as IDBRequest<T[]>),
  put: (card: { id: string }) =>
    tx(STORE_CARDS, 'readwrite', (s) => s.put(card) as IDBRequest<IDBValidKey>),
  /** Пакетний запис — щоб не відкривати транзакцію на кожну картку. */
  putMany: async (items: { id: string }[]) => {
    const db = await open()
    return new Promise<void>((resolve, reject) => {
      const t = db.transaction(STORE_CARDS, 'readwrite')
      const store = t.objectStore(STORE_CARDS)
      for (const item of items) store.put(item)
      t.oncomplete = () => resolve()
      t.onerror = () => reject(t.error)
    })
  },
  clear: () => tx(STORE_CARDS, 'readwrite', (s) => s.clear() as IDBRequest<undefined>),
}

export interface LogEntry {
  /** Момент оцінки — водночас ключ запису. */
  at: number
  phraseId: string
  grade: string
  /** Скільки мілісекунд думав. */
  ms: number
}

export const log = {
  add: (entry: LogEntry) =>
    tx(STORE_LOG, 'readwrite', (s) => s.put(entry) as IDBRequest<IDBValidKey>).catch(() => undefined),
  all: () => tx<LogEntry[]>(STORE_LOG, 'readonly', (s) => s.getAll() as IDBRequest<LogEntry[]>),
  clear: () => tx(STORE_LOG, 'readwrite', (s) => s.clear() as IDBRequest<undefined>),
}

export const recordings = {
  get: (id: string) =>
    tx<Blob | undefined>(STORE_AUDIO, 'readonly', (s) => s.get(id) as IDBRequest<Blob | undefined>),
  put: (id: string, blob: Blob) =>
    tx(STORE_AUDIO, 'readwrite', (s) => s.put(blob, id) as IDBRequest<IDBValidKey>),
  del: (id: string) => tx(STORE_AUDIO, 'readwrite', (s) => s.delete(id) as IDBRequest<undefined>),
  keys: () => tx<IDBValidKey[]>(STORE_AUDIO, 'readonly', (s) => s.getAllKeys()),
}

/** Чи взагалі доступна IndexedDB (приватний режим Safari буває капризним). */
export async function available(): Promise<boolean> {
  try {
    await open()
    return true
  } catch {
    return false
  }
}
